import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import React from "react";

import { deleteUser as deleteUserAPI } from "api/users";
import { usersKeys } from "api/users/queries";
import { UsersType } from "api/users/types";
import UltimateButton from "components/UltimateButton";

interface DeleteUserModalProps {
  isOpen: boolean;
  handleClose: () => void;
  user: UsersType | undefined;
}

const DeleteUserModal = ({
  isOpen,
  handleClose,
  user,
}: DeleteUserModalProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteUser, status } = useMutation(
    async (userId: string) => {
      const res = await deleteUserAPI(userId);
      await queryClient.invalidateQueries(usersKeys.list());
      return res.data;
    },
  );

  const handleDelete = async () => {
    if (!user?._id) return;
    await deleteUser(user._id, {
      onSuccess: () => handleClose(),
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          py: 2,
          pb: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={1.5}
            sx={{
              bgcolor: (t) =>
                (t.palette as any).brandDanger?.secondary ?? "#FEE9E8",
              borderRadius: "50%",
            }}
          >
            <Trash2
              size={32}
              color={(theme.palette as any).brandDanger?.primary ?? "#EC1F3D"}
            />
          </Stack>
          <Typography variant="headingMD" fontWeight={600}>
            Delete user
          </Typography>
          <Typography
            variant="textSM"
            textAlign="center"
            sx={{
              color: (t) =>
                (t.palette as any).brandText?.secondary ?? "#A2A8B6",
            }}
          >
            Are you sure you want to delete{" "}
            <span
              style={{
                color: (theme.palette as any).brandText?.primary ?? "#111726",
              }}
            >
              {user?.email ?? "this user"}
            </span>
            ? This action cannot be undone.
          </Typography>
        </Stack>
      </DialogTitle>

      <Stack width="100%" alignItems="center" justifyContent="center">
        <UltimateButton
          text="Delete"
          color="brandDanger"
          Icon={
            Trash2 as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          onClick={handleDelete}
          loading={status === "loading"}
          disabled={!user?._id}
          sx={{ py: 1 }}
        />
      </Stack>
    </Dialog>
  );
};

export default DeleteUserModal;
